/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { AxiosInstance } from '../axios.config';
import { api } from '../constants';
import {
  IAddOnDetail,
  IAddOnListResponse,
  IProduct,
  IProductCategory,
  IProductDetail,
  IRestockAddOnItem,
  IVariantApiResponse,
  ProductCount,
} from './types';
import { AxiosResponse } from 'axios';

export interface IGetProductParams {
  status?: string;
  page?: number;
  start_date?: string;
  end_date?: string;
  search?: string;
  query: string;
}
export interface IGetOrderParams {
  status?: string;
  page?: number;
  start_date?: string;
  page_size?: number;
  end_date?: string;
  q?: string;
}

type ProductCategoryWithoutChild = Omit<IProductCategory, 'specific_child'>;
type ProductSubCategoryWithoutChild = Omit<IProductCategory, 'specific_child'>;

const getCategoriesForFilter = (): Promise<AxiosResponse<ProductCategoryWithoutChild[]>> => {
  return AxiosInstance.get(api.manageProduct.filterCategories);
};

const useGetCategoriesForFilter = (
  options?: UseQueryOptions<ProductCategoryWithoutChild[]>,
): UseQueryResult<ProductCategoryWithoutChild[] | undefined> => {
  return useQuery({
    queryKey: ['categoriesForFilter'],
    queryFn: async () => {
      const response = await getCategoriesForFilter();
      return response.data;
    },
    ...options,
  });
};

const getSubCategoriesForFilter = (
  id: number,
): Promise<AxiosResponse<ProductSubCategoryWithoutChild[]>> => {
  return AxiosInstance.get(api.manageProduct.filterSubCategories.replace(':id', id.toString()));
};

const useGetSubCategoriesForFilter = (
  id?: number,
  options?: UseQueryOptions<ProductSubCategoryWithoutChild[]>,
): UseQueryResult<ProductSubCategoryWithoutChild[] | undefined> => {
  return useQuery({
    queryKey: ['subCategoriesForFilter', id],
    queryFn: async () => {
      if (!id) throw new Error('Category ID is required');
      const response = await getSubCategoriesForFilter(id);
      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

const getProducts = async (
  params: IGetProductParams,
): Promise<{ results: IProduct[]; pageSize: number; count: number }> => {
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(([value]) => value != null && value !== ''),
  );
  const response = await AxiosInstance.get(api.manageProduct.list, {
    params: filteredParams,
  });

  return response.data;
};

const useGetProducts = (
  params: IGetProductParams,
): UseQueryResult<{
  results: IProduct[];
  pageSize: number;
  count: number;
  page_count: number;
}> => {
  return useQuery({
    queryKey: ['Products', params],
    queryFn: () => getProducts(params),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: false,
  });
};

const deleteProduct = async (id: number): Promise<AxiosResponse> => {
  return AxiosInstance.delete(api.manageProduct.delete.replace(':id', id.toString()));
};

const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['deleteProduct'],
    mutationFn: (id: number) => deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Products'] });
      queryClient.invalidateQueries({ queryKey: ['productCount'] });
    },
  });
};

const getProductById = async (id: string): Promise<IProductDetail> => {
  const response = await AxiosInstance.get(api.manageProduct.product_detail.replace(':id', id));
  return response.data;
};

const useGetProductDetail = (id: string): UseQueryResult<IProductDetail> => {
  return useQuery({
    queryKey: ['product_detail', id],
    queryFn: () => getProductById(id),
    enabled: !!(id && id !== '0'),
  });
};

const restockAddOn = async (payload: IRestockAddOnItem[]): Promise<any> => {
  const response = await AxiosInstance.post(api.manageProduct.addonsRestock, payload);
  return response.data;
};

export const useRestockAddOn = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['restockAddOn'],
    mutationFn: restockAddOn,
    onSuccess: (_, payload: IRestockAddOnItem[]) => {
      queryClient.invalidateQueries({ queryKey: ['addOns'] });

      payload.forEach((item) => {
        queryClient.invalidateQueries({
          queryKey: ['addOnDetail', String(item.add_on_id)],
        });
      });

      // Optional: also invalidate global counts if restock affects them
      queryClient.invalidateQueries({ queryKey: ['productCount'] });
    },
    onError: (error) => {
      console.error('Add-on restock failed:', error);
    },
  });
};

const getVariants = async (productId: number): Promise<IVariantApiResponse> => {
  const response = await AxiosInstance.get(
    api.manageProduct.variants.replace(':product_id', productId.toString()),
  );
  return response.data;
};

const useGetVariants = (
  productId: number | null,
  options?: UseQueryOptions<IVariantApiResponse>,
): UseQueryResult<IVariantApiResponse> => {
  return useQuery({
    queryKey: ['variants', productId],
    queryFn: () => getVariants(productId!),
    enabled: !!productId,
    ...options,
  });
};

const getAddOns = async (params: {
  status?: string;
  page?: number;
  page_size?: number;
  search?: string;
  start_date?: string;
  end_date?: string;
  category?: string | number;
  subcategory?: string | number;
  ordering?: string;
}): Promise<IAddOnListResponse> => {
  const finalParams = {
    ...params,
    is_add_on: true,
  };
  const response = await AxiosInstance.get(api.manageProduct.addOnsList, {
    params: {
      status: finalParams.status,
      page: finalParams.page ?? 1,
      page_size: finalParams.page_size ?? 10,
      search: finalParams.search ?? '',
      start_date: finalParams.start_date,
      end_date: finalParams.end_date,
      category: finalParams.category,
      subcategory: finalParams.subcategory,
      ordering: finalParams.ordering ?? '-created_at',
      is_add_on: finalParams.is_add_on,
    },
  });
  return response.data;
};

export const useGetAddOns = (
  params: {
    status?: string;
    page: number;
    search: string;
    start_date?: string;
    end_date?: string;
    category?: string | number;
    subcategory?: string | number;
  },
  options?: UseQueryOptions<IAddOnListResponse>,
): UseQueryResult<IAddOnListResponse> => {
  return useQuery({
    queryKey: ['addOns', { ...params, is_add_on: true }],
    queryFn: () => getAddOns(params),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

const deleteAddOn = async (id: number): Promise<void> => {
  await AxiosInstance.delete(api.manageProduct.deleteAddOn.replace(':id', id.toString()));
};

export const useDeleteAddOn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAddOn,
    onSuccess: (_, id: number) => {
      queryClient.invalidateQueries({ queryKey: ['addOns'] });
      queryClient.invalidateQueries({ queryKey: ['productCount'] });
      queryClient.removeQueries({ queryKey: ['addOnDetail', String(id)] });
    },
  });
};

const getAddOnDetail = async (id: string): Promise<IAddOnDetail> => {
  const response = await AxiosInstance.get(api.manageProduct.addOnDetail.replace(':id', id));
  return response.data;
};

export const useGetAddOnDetail = (
  id: string | undefined,
  options?: UseQueryOptions<IAddOnDetail>,
): UseQueryResult<IAddOnDetail> => {
  return useQuery({
    queryKey: ['addOnDetail', id],
    queryFn: () => getAddOnDetail(id!),
    enabled: Boolean(id) && id !== '0' && id !== 'undefined',
    ...options,
  });
};

export const useGetProductCount = (
  filters: {
    start_date?: string;
    end_date?: string;
    category?: string | number;
    subcategory?: string | number;
  } = {},
) => {
  return useQuery<ProductCount, Error>({
    queryKey: ['productCount', filters],
    queryFn: async () => {
      const params = {
        is_add_on: true,
        ...(filters.start_date && { start_date: filters.start_date }),
        ...(filters.end_date && { end_date: filters.end_date }),
        ...(filters.category && { category: filters.category }),
        ...(filters.subcategory && { subcategory: filters.subcategory }),
      };

      const { data } = await AxiosInstance.get(api.manageProduct.addonCount, { params });
      return data;
    },
    staleTime: 60_000,
  });
};

export {
  useGetProducts,
  useGetCategoriesForFilter,
  useGetSubCategoriesForFilter,
  useDeleteProduct,
  useGetProductDetail,
  useGetVariants,
};
